import { useEffect, useState } from "react";
import { Copy, Share2, ShoppingBasket, Check } from "lucide-react";
import { toast } from "sonner";
import { PHASES } from "@/lib/lunelle-data";
import { groceryFor } from "@/lib/recipes";
import { useTodayPhase } from "@/lib/cycle";
import { useAccount } from "@/lib/account";

const DINNERS = "3 dinners: comfort, restaurant-style and a quick 15-minute one. Serves 2 each.";

const asText = (phaseName, groups) => [`Lunelle grocery list · ${phaseName} week`, DINNERS, "",
  ...groups.flatMap(([section, items]) => [section.toUpperCase(), ...items.map((i) => `- ${i}`), ""])].join("\n").trim();

const SignedOut = () => {
  const { openAuth } = useAccount();
  return (
    <div className="mt-8 flex flex-col gap-4 rounded-3xl border border-dashed border-[#C86D51]/40 p-6 sm:flex-row sm:items-center sm:justify-between" data-testid="grocery-signed-out">
      <p className="flex items-center gap-3 text-[#3D302B]"><ShoppingBasket className="h-5 w-5 shrink-0 text-[#9E4429]" />Get a weekly grocery list for your phase, matched to your faith.</p>
      <button type="button" onClick={() => openAuth("register")} data-testid="grocery-signup-button" className="shrink-0 rounded-full bg-[#2C221E] px-5 py-2.5 text-sm font-semibold text-[#FBF5EE] hover:bg-[#9E4429]">Create a free account</button>
    </div>
  );
};

// Members: one tap-to-copy shopping list for a phase's three dinners
export const GroceryList = ({ faith }) => {
  const { user } = useAccount();
  const today = useTodayPhase()?.phase;
  const [phase, setPhase] = useState("follicular");
  const [ticked, setTicked] = useState({});
  useEffect(() => { if (today) setPhase(today); }, [today]);
  useEffect(() => { setTicked({}); }, [phase, faith]);
  if (!user) return <SignedOut />;
  const ph = PHASES.find((p) => p.id === phase);
  const groups = groceryFor(phase, faith);
  const text = asText(ph.name, groups);
  const copy = () => navigator.clipboard.writeText(text).then(() => toast("Grocery list copied", { description: "Paste it into your notes or messages." })).catch(() => toast.error("Couldn't copy. Try the share button."));
  const share = () => navigator.share({ title: `Lunelle grocery list · ${ph.name} week`, text }).catch(() => {});

  return (
    <div className="mt-8 rounded-3xl border border-[#C86D51]/20 bg-[#FDFBF7]/80 p-5 sm:p-6" data-testid="grocery-list">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#9E4429]"><ShoppingBasket className="h-4 w-4" />Weekly grocery list</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {PHASES.map((p) => (
          <button key={p.id} type="button" onClick={() => setPhase(p.id)} data-testid={`grocery-phase-${p.id}`}
            className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${phase === p.id ? "text-[#FBF5EE]" : "border-[#C86D51]/25 text-[#5C4A42] hover:border-[#9E4429]"}`}
            style={phase === p.id ? { background: p.color, borderColor: p.color } : undefined}>{p.name}{p.id === today ? " · now" : ""}</button>
        ))}
      </div>
      <p className="mt-3 text-sm text-[#6B5A52]">{ph.name} week · {DINNERS}</p>
      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        {groups.map(([section, items]) => (
          <div key={section} data-testid={`grocery-section-${section.split(" ")[0].toLowerCase()}`}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#7A665C]">{section}</p>
            <ul className="mt-2 space-y-1">
              {items.map((item) => (
                <li key={item}>
                  <label className="flex cursor-pointer items-start gap-2.5 py-1 text-sm text-[#2C221E]">
                    <input type="checkbox" checked={Boolean(ticked[item])} onChange={() => setTicked((t) => ({ ...t, [item]: !t[item] }))} className="mt-0.5 h-4 w-4 shrink-0 accent-[#9E4429]" data-testid="grocery-item-checkbox" />
                    <span className={ticked[item] ? "text-[#8a766c] line-through" : ""}>{item}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <button type="button" onClick={copy} data-testid="grocery-copy-button" className="inline-flex items-center gap-2 rounded-full bg-[#2C221E] px-5 py-2.5 text-sm font-semibold text-[#FBF5EE] hover:bg-[#9E4429]"><Copy className="h-4 w-4" />Copy list</button>
        {typeof navigator !== "undefined" && navigator.share && (
          <button type="button" onClick={share} data-testid="grocery-share-button" className="inline-flex items-center gap-2 rounded-full border border-[#C86D51]/30 px-5 py-2.5 text-sm font-semibold text-[#2C221E] hover:border-[#9E4429]"><Share2 className="h-4 w-4" />Share</button>
        )}
        {Object.values(ticked).some(Boolean) && <span className="inline-flex items-center gap-1.5 text-sm text-[#3F5E57]" data-testid="grocery-ticked-count"><Check className="h-4 w-4" />{Object.values(ticked).filter(Boolean).length} in the basket</span>}
      </div>
    </div>
  );
};
